import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

// services
import { TranslateService } from '@ngx-translate/core';
import {
  NgbDateParserFormatter,
  NgbDropdown,
  NgbDate,
} from '@ng-bootstrap/ng-bootstrap';
import {
  CollectionViewModel,
  ProfileCollectionFacadeV1,
  StatusVM,
} from '@app/profile/components/profile-collection-v1/profile-collection.facade';
import { Observable } from 'rxjs';
import { ngbDateToJsDate } from '@app/shared/utils/time-utils';
import { MediumDateParserFormatterService } from '@app/shared/services/date-picker/medium-date-parser-formatter.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';

@Component({
  selector: 'dgx-date-facet-filter-v1',
  templateUrl: './date-facet-filter.component.html',
  styleUrls: [
    './date-facet-filter.component.scss',
    '../profile-collection-filters.component.scss',
  ],
  providers: [
    {
      provide: NgbDateParserFormatter,
      useClass: MediumDateParserFormatterService,
    },
  ],
})
export class DateFacetFilterV1
  extends SubscriberBaseDirective
  implements OnInit
{
  @ViewChild('ngbDropdown')
  public ngbDropdown: NgbDropdown;

  @ViewChild('filterToggle')
  public filterToggle: ElementRef;

  public currentDate: Date = new Date();
  public vm$: Observable<CollectionViewModel>;
  public status$: Observable<StatusVM>;
  public appliedFilter: { startDate: Date; endDate: Date };
  public i18n = this.translateService.instant([
    'Core_Date',
    'Core_StartDate',
    'Core_EndDate',
    'Core_ShowResults',
    'Core_ResetDateFilters',
    'Core_Clear',
    'Core_DateAddedToProfile',
    'PositionFormCtrl_EndAfterStart',
  ]);
  public form: FormGroup;

  constructor(
    private translateService: TranslateService,
    private profileCollectionFacade: ProfileCollectionFacadeV1,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.form = new FormGroup({
      dateRange: new FormGroup(
        {
          startDate: new FormControl(null, Validators.required),
          endDate: new FormControl(null, Validators.required),
        },
        { validators: this.dateRangeValidator }
      ),
    });
  }

  public ngOnInit(): void {
    this.vm$ = this.profileCollectionFacade.vm$;
    this.status$ = this.profileCollectionFacade.status$;
    this.profileCollectionFacade.vm$
      .pipe(this.takeUntilDestroyed())
      .subscribe((vm) => {
        if (vm.filterToFocus === 'date') {
          this.filterToggle?.nativeElement.focus();
        }
        const { startDate, endDate } = vm.filter;
        if (startDate && endDate) {
          this.form.get('dateRange').setValue({
            startDate: startDate,
            endDate: endDate,
          });
        }
        this.appliedFilter = {
          startDate,
          endDate,
        };
      });
  }

  private dateRangeValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const startDate = control.get('startDate').value;
    const endDate = control.get('endDate').value;

    if (!startDate || !endDate) {
      return null;
    }

    return ngbDateToJsDate(endDate) >= ngbDateToJsDate(startDate)
      ? null
      : { dateRange: true };
  };

  public resetDates(): void {
    this.form.reset({
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });

    if (!this.appliedFilter.startDate && !this.appliedFilter.endDate) {
      this.ngbDropdown.close();
      return;
    }

    this.applyDateFilter(true);
  }

  public applyDateFilter(reset?): void {
    if (this.form.valid || reset) {
      const dateRange = this.form.get('dateRange').value;
      this.profileCollectionFacade.updateDateFilterValues(
        dateRange.startDate
          ? ngbDateToJsDate(dateRange.startDate).toString()
          : null,
        dateRange.endDate ? ngbDateToJsDate(dateRange.endDate).toString() : null
      );
      this.appliedFilter = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
    }
  }
}
